import React, { ReactNode } from 'react';

import { FormikProps } from 'formik';
import {
  Box,
  FilledInput,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  Theme,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SxProps, SystemStyleObject } from '@mui/system';
import { Constants, TextFieldVariants } from '../../../helpers/Constants';
import IDropdownObject from '../../../types/ICustomDropdown';

import Checkbox from '../Checkbox';
import useTextField from '../../../hooks/useTextField';
import useDropdownDragSelect from '../../../hooks/useDropdownDragSelect';
import classes from './style';

type ICustomDropdown = {
  usePreFilledColors?: boolean;
  id?: string;
  name: string;
  menuItems: Array<IDropdownObject>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikProps<any>;
  placeholder?: string;
  label?: string;
  fullWidth?: boolean;
  isRecurrence?: boolean;
  customClass?: Record<string, unknown>;
  horizontal?: boolean;
  onClickMenuItem?: (val: string) => void;
  disabled?: boolean;
  multiple?: boolean;
  isDragSelectAllowed?: boolean;
  imageMenu?: boolean;
  requireLabel?: boolean;
  showPlaceholder?: boolean;
  radioDropdown?: boolean;
  errorContainer?: boolean;
  customMenuStyles?: SxProps<Theme>;
  labelStyles?: SxProps<Theme>;
  variant?: TextFieldVariants;
  onClose?: () => void;
};

const defaultProps = {
  id: '',
  fullWidth: true,
  label: '',
  placeholder: Constants.null,
  customClass: {},
  horizontal: false,
  multiple: false,
  isDragSelectAllowed: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onClickMenuItem: () => {},
  disabled: false,
  imageMenu: false,
  isRecurrence: false,
  requireLabel: false,
  showPlaceholder: false,
  errorContainer: true,
};

const CustomDropdown = ({
  usePreFilledColors,
  id,
  label,
  name,
  formik,
  menuItems,
  placeholder,
  fullWidth,
  customClass,
  multiple,
  isDragSelectAllowed,
  horizontal,
  requireLabel,
  onClickMenuItem,
  disabled,
  imageMenu,
  isRecurrence,
  showPlaceholder,
  radioDropdown,
  errorContainer,
  customMenuStyles,
  labelStyles,
  variant,
  onClose,
}: ICustomDropdown) => {
  const errors = formik?.errors[name] && formik?.touched[name] && formik?.errors[name];
  const initValue = multiple || imageMenu ? [] : '';
  const fieldValue = formik?.values?.[name];
  const isEmptyValue = fieldValue === ''
    || fieldValue === null
    || fieldValue === undefined;

  const parseValue = (value: any) =>
    (typeof value === 'object' && !multiple && value !== null
      ? JSON.stringify(value)
      : value);

  const { style } = useTextField({
    value: parseValue(fieldValue) ?? initValue,
    usePreFilledColors,
  });

  const {
    handleDragStart,
    handleDragEnter,
    handleClickCapture,
  } = useDropdownDragSelect({
    isEnabled: !!multiple && !!isDragSelectAllowed,
    selectedKeys: fieldValue ?? [],
    onChange: (keys) => {
      formik?.setFieldValue(name, keys);
    },
  });

  const handleValueRender = (renderValue: any) => {
    const itemValue = parseValue(renderValue);
    const isArray = typeof menuItems?.[0] === 'string';
    const placeHolder: (string | undefined)[] = [];
    const getLabel = (selected: any) => {
      if (isArray) {
        placeHolder.push(selected);
      } else {
        const itemLabel = menuItems?.find(
          (item) =>
            parseValue(item.key) === selected,
        )?.label;
        placeHolder.push(`${itemLabel}`);
      }
    };
    switch (true) {
      case multiple && !imageMenu:
        itemValue?.forEach((selectedItem: any) => {
          getLabel(selectedItem);
        });
        return placeHolder.join(', ');
      case multiple && imageMenu: {
        itemValue?.forEach((selectedItem: any) =>
          getLabel(selectedItem));
        const imagesArray = [] as any;
        placeHolder.forEach((item) => {
          imagesArray.push(
            <img style={classes.imageMenuItem} src={item} alt="dropdown" />,
          );
        });
        if (imagesArray?.length > 0) {
          return imagesArray.map((image: any) =>
            image);
        }
        return null;
      }
      case showPlaceholder && isEmptyValue:
        return placeholder;
      default: {
        getLabel(itemValue);
        return placeHolder.join(', ');
      }
    }
  };

  const isFilled = variant === TextFieldVariants.filled;
  const input = isFilled ? (
    <FilledInput placeholder={placeholder} />
  ) : (
    <OutlinedInput />
  );

  return (
    <>
      <Box
        id={id}
        component="div"
        sx={[
          horizontal ? classes.horizontalDropdown : {},
          customClass as SystemStyleObject,
        ]}
      >
        {label && (
          <InputLabel>
            {label}
            {requireLabel && (
              <Box component="span" sx={classes.customTextFieldRequireLabel}>
                *
              </Box>
            )}
          </InputLabel>
        )}

        <Select
          sx={[
            isEmptyValue && showPlaceholder
              ? classes.placeholderText
              : {},
            {
              '& .MuiSelect-select': style,
            },
            isFilled ? classes.customFilledInput : {},
          ]}
          fullWidth={fullWidth}
          disabled={disabled}
          SelectDisplayProps={{
            style: imageMenu ? classes.imageStyle : {},
          }}
          value={parseValue(fieldValue) ?? initValue}
          multiple={multiple}
          MenuProps={{
            ...(isRecurrence
              ? null
              : {
                sx: [classes.removeFocus, customMenuStyles] as SxProps<Theme>,
              }),
          }}
          error={!!errors}
          displayEmpty={showPlaceholder}
          IconComponent={ExpandMoreIcon}
          onChange={(e) =>
            formik?.setFieldValue(name, e.target.value)}
          input={input}
          renderValue={handleValueRender}
          placeholder={placeholder}
          onClose={onClose}
        >
          {menuItems?.map((item) => {
            const itemLabel = item.label || item;
            return (
              <MenuItem
                key={item.key}
                sx={{ color: 'grey.800' }}
                value={parseValue(item.key) || item.key}
                onClick={(e) =>
                  onClickMenuItem?.(e.currentTarget?.dataset?.value ?? '')}
                onMouseDown={handleDragStart(item.key)}
                onMouseEnter={handleDragEnter(item.key)}
                onClickCapture={handleClickCapture}
              >
                <>
                  {multiple && (
                    <Checkbox
                      checked={formik?.values[name]?.includes(item?.key)}
                    />
                  )}
                  {radioDropdown && (
                    <Radio
                      checked={formik?.values[name]?.includes(item?.key)}
                    />
                  )}
                  {imageMenu && (
                    <img
                      style={classes.imageMenuItem}
                      src={`${item.label}`}
                      alt="menu Item"
                    />
                  )}
                  <Box sx={classes.items}>
                    <Box component="div" sx={labelStyles}>
                      {(!imageMenu && itemLabel) as string}
                    </Box>
                    {item?.subtitle && (
                      <Typography
                        sx={
                          [classes.subtitleText, item?.subtitleStyle] as SxProps
                        }
                      >
                        {item?.subtitle}
                      </Typography>
                    )}
                  </Box>
                </>
              </MenuItem>
            );
          })}
        </Select>
      </Box>
      {errorContainer && (
        <FormHelperText style={classes.error}>
          {errors as ReactNode}
        </FormHelperText>
      )}
    </>
  );
};

CustomDropdown.defaultProps = defaultProps;

export default CustomDropdown;
